/**
 * @file Выбор одного файла через системный диалог или drag-and-drop.
 *
 * После выбора передаёт файл в Provider. Подготовка данных и сетевой прогресс
 * не относятся к состоянию самой кнопки.
 */
import { useId, useRef, useState } from 'react'
import type { DragEvent } from 'react'
import { FaFileMedical } from 'react-icons/fa'

import { useLocale } from '../../i18n/useLocale'
import './Upload.css'
import { uploadMessages } from './messages'
import { isUploadBusy } from './model'
import { useUpload } from './useUpload'

export default function UploadControl({
    boxId,
    folder,
    token,
    password,
    disabled,
}: {
    boxId: string
    folder: string
    token: string
    password: string
    disabled: boolean
}) {
    // Общая задача живёт в Provider; язык определяет только подписи кнопки.
    const { start, state } = useUpload()
    const { locale } = useLocale()
    const text = uploadMessages[locale]

    // Системный input скрыт. Видимая кнопка обращается к нему через ref.
    const input = useRef<HTMLInputElement>(null)
    const hintId = useId()

    // Локальное состояние выбора: это ещё не прогресс передачи файла.
    const [error, setError] = useState<'oneFile' | 'busy' | null>(null)
    const [dragging, setDragging] = useState(false)

    const busy = isUploadBusy(state)

    function select(file: File | undefined) {
        if (!file || disabled) return

        setError(null)
        if (!start({ file, boxId, folder, token, password })) setError('busy')
    }

    function drop(event: DragEvent<HTMLDivElement>) {
        event.preventDefault()
        setDragging(false)
        if (disabled) return

        if (busy) {
            setError('busy')

            return
        }

        if (
            event.dataTransfer.files.length !== 1 ||
            Array.from(event.dataTransfer.items).some(
                (item) => item.webkitGetAsEntry?.()?.isDirectory,
            )
        ) {
            setError('oneFile')

            return
        }

        select(event.dataTransfer.files[0])
    }

    return (
        <div
            className={`upload-picker${dragging ? ' upload-picker-dragging' : ''}`}
            onDragOver={(event) => {
                event.preventDefault()
                if (!disabled && !busy) setDragging(true)
            }}
            onDragLeave={(event) => {
                const next = event.relatedTarget
                if (
                    !(next instanceof Node) ||
                    !event.currentTarget.contains(next)
                )
                    setDragging(false)
            }}
            onDrop={drop}
        >
            {/* Настоящий file input открывает системный диалог, но оформленная кнопка показана отдельно. */}
            <input
                ref={input}
                type='file'
                hidden
                disabled={disabled || busy}
                aria-label={text.choose}
                onChange={(event) => {
                    select(event.target.files?.[0])
                    event.target.value = ''
                }}
            />

            {/* Диалог и drag-and-drop вызывают один select: оба пути подчиняются блокировке в Provider. */}
            <button
                type='button'
                disabled={disabled || busy}
                aria-describedby={hintId}
                title={busy ? text.busy : text.drop}
                onClick={() => input.current?.click()}
            >
                <FaFileMedical aria-hidden='true' />
                {text.choose}
            </button>

            {/* Подсказка доступна скринридеру через aria-describedby и не занимает место в панели. */}
            <span id={hintId} className='upload-picker-hint'>
                {busy ? text.busy : text.drop}
            </span>

            {/* Ошибка выбора файла относится к этой кнопке, а сетевые ошибки — к панели передачи. */}
            {error && <p role='alert'>{text[error]}</p>}
        </div>
    )
}
